import type { UseQueryOptions, UseQueryResult } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';

import type { IQueryKeyFactory } from '~/helpers/types';
import type { ApiError } from '~/lib/api';
import type {
  IApiGetPicturesRequestBody,
  IApiGetPicturesResponseBody,
} from '../PicturesApi.types';
import { useApi } from '~/lib/api/useApi';
import { pictureQueryKeyFactory } from './usePictureQuery';

export type IUsePicturesQueryValues = {
  appId: string;
  body?: IApiGetPicturesRequestBody;
};

export const picturesQueryKeyFactory: IQueryKeyFactory<
  IUsePicturesQueryValues
> = (values) =>
  values &&
  (values.body
    ? (['pictures', values.appId, values.body] as const)
    : (['pictures', values.appId] as const));

export const usePicturesQuery = (
  values?: IUsePicturesQueryValues,
  options?: UseQueryOptions<IApiGetPicturesResponseBody, ApiError>,
): UseQueryResult<IApiGetPicturesResponseBody, ApiError> => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: picturesQueryKeyFactory(values),
    queryFn: values
      ? () => api.pictures.getAll(values.appId, values.body)
      : undefined,
    onSuccess: (pictures) => {
      pictures.list?.forEach((picture) => {
        const queryKey = pictureQueryKeyFactory({
          appId: picture.appId,
          pictureId: picture._id,
        });
        if (queryKey) {
          queryClient.setQueryData(queryKey, picture);
        }
      });
      options?.onSuccess?.(pictures);
    },
    ...options,
  });
};
